import {Filter} from '@github-ui/filter'
import {useCallback} from 'react'

import {LABELS} from '../../constants'
import {SAVED_VIEW_UPDATE_SOURCES, useSavedViewAnalytics} from '../../hooks/use-saved-view-analytics'
import {useSavedViewFilterProviders} from '../../hooks/use-saved-view-filter-providers'
import {useSavedViewFilterSettings} from '../../hooks/use-saved-view-filter-settings'
import type {EditableShortcutField} from '../../hooks/use-shortcut-editor'
import {useUpdateSavedViewMutation} from '../../hooks/use-update-saved-view-mutation'
import type {SavedView, UserShortcut} from '../../types'
import {SavedViewMutationErrorBanner} from '../dialogs/SavedViewMutationErrorBanner'
import {ShortcutEditingHeader} from '../ShortcutEditingHeader'
import styles from './SavedViewEditHeader.module.css'
import {VisibilityValue} from './VisibilityValue'

interface SavedViewEditHeaderProps {
  savedView: SavedView
  owner: string
  repo: string
  shortcut: UserShortcut
  setShortcutValue: (field: EditableShortcutField, value: string) => void
  onPreviewQuerySubmit: (next: string) => void
  onSaved: () => void
  onCancel: () => void
  visibilityEnabled: boolean
}

export function SavedViewEditHeader({
  savedView,
  owner,
  repo,
  shortcut,
  setShortcutValue,
  onPreviewQuerySubmit,
  onSaved,
  onCancel,
  visibilityEnabled,
}: SavedViewEditHeaderProps) {
  const filterProviders = useSavedViewFilterProviders({owner, repo})
  const filterSettings = useSavedViewFilterSettings()

  const {mutate, isPending, error} = useUpdateSavedViewMutation({owner, repo})
  const {trackViewUpdated} = useSavedViewAnalytics()

  const handleSave = useCallback(() => {
    mutate(
      {
        id: savedView.id,
        name: shortcut.name.trim(),
        query: shortcut.query.trim(),
        description: shortcut.description?.trim() ?? '',
        icon: shortcut.icon,
        color: shortcut.color,
      },
      {
        onSuccess: () => {
          trackViewUpdated(savedView.id, savedView.visibility, SAVED_VIEW_UPDATE_SOURCES.edit)
          onSaved()
        },
      },
    )
  }, [mutate, savedView.id, savedView.visibility, shortcut, onSaved, trackViewUpdated])

  return (
    <div className={styles.editHeaderWrapper}>
      <SavedViewMutationErrorBanner
        message={error ? LABELS.views.updateError : null}
        onRetry={handleSave}
        isRetrying={isPending}
      />
      <ShortcutEditingHeader
        shortcut={shortcut}
        setShortcutValue={setShortcutValue}
        onSave={handleSave}
        onDismiss={onCancel}
        isSaving={isPending}
        trailingField={visibilityEnabled ? <VisibilityValue savedView={savedView} /> : undefined}
        renderFilter={({inputRef, className}) => (
          <Filter
            id="saved-view-edit-filter"
            className={className}
            inputRef={inputRef}
            label={LABELS.views.filterQueryLabel}
            placeholder={LABELS.views.filterQueryPlaceholder}
            variant="input"
            providers={filterProviders}
            filterValue={shortcut.query}
            onChange={value => setShortcutValue('query', value)}
            onSubmit={request => onPreviewQuerySubmit(request.raw)}
            settings={filterSettings}
          />
        )}
      />
    </div>
  )
}
